'use client'

import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'
import { SITE_CONTAINER } from '@/lib/site-layout'
import styles from './GooglePreferredSourceHeader.module.css'

type PreferredSourceApi = {
  init: (options: { theme: 'dark'; lang: 'tr' }) => void
}

type PreferredSourceWindow = Window & {
  PREFERRED_SOURCE?: { push: (callback: (api: PreferredSourceApi) => void) => unknown }
}

export default function GooglePreferredSourceHeader({ nonce }: { nonce?: string }) {
  const buttonRef = useRef<HTMLDivElement>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const element = buttonRef.current
    if (!element) return
    let observedShadow: ShadowRoot | null = null
    const update = () => {
      const shadow = element.shadowRoot
      if (shadow && shadow !== observedShadow) {
        observedShadow = shadow
        observer.observe(shadow, { childList: true, subtree: true })
      }
      setReady(Boolean(shadow?.querySelector('publisher-md-outlined-button, button')) || element.childElementCount > 0)
    }
    const observer = new MutationObserver(update)
    observer.observe(element, { childList: true, attributes: true, attributeFilter: ['data-initialized'] })
    update()
    return () => observer.disconnect()
  }, [])

  const initialize = () => {
    const sourceWindow = window as PreferredSourceWindow
    sourceWindow.PREFERRED_SOURCE?.push((api) => {
      if (!buttonRef.current?.isConnected) return
      try {
        api.init({ theme: 'dark', lang: 'tr' })
      } catch {
        // Keep the direct Google preferences link if the external widget fails.
      }
    })
  }

  return (
    <div className={`${SITE_CONTAINER} ${styles.wrap}`}>
      <aside className={styles.banner} aria-label="Google'da takip et">
        <div className={styles.text}>
          <strong>Haber.com&apos;u Google&apos;a ekleyin, gündemi kaçırmayın</strong>
          <span>Tercih ettiğiniz kaynaklara ekleyin, Google&apos;da haberlerimizi daha sık görün.</span>
        </div>
        <div className={styles.cta}>
          <div
            ref={buttonRef}
            google-add-preferred-source-btn=""
            data-theme="dark"
            data-lang="tr"
            className={styles.googleButton}
          />
          {!ready && (
            <a
              className={styles.fallback}
              href="https://www.google.com/preferences/source?q=https%3A%2F%2Fwww.haber.com%2F"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google&apos;a ekle
            </a>
          )}
        </div>
      </aside>
      <Script
        id="google-preferred-source-publisher"
        src="https://news.google.com/swg/js/v1/publisher.js"
        strategy="afterInteractive"
        nonce={nonce}
        preferred-sources-control="manual"
        onReady={initialize}
      />
    </div>
  )
}
